import {
  Link,
  Outlet,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router'
import {
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Download,
  KeyRound,
  LogOut,
  Menu,
  MonitorSmartphone,
  CircleHelp,
  ServerCog,
  Search,
  UserRound,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  ThemeToggle,
  Tooltip,
  cn,
  useScopedKeydown,
} from '@aeris/ui'

import { useAuth } from '../lib/auth-context.ts'
import { desktopShellAvailable, setPcMode } from '../lib/desktop.ts'
import {
  labelCount,
  readLabelQueue,
  subscribeToLabelQueue,
} from '../lib/label-queue.ts'
import {
  COUNTER_MODULES,
  MODULE_GROUPS,
  MODULES,
  type ModuleEntry,
  type ModuleGroup,
} from '../lib/modules.tsx'
import { sidebarSections } from '../lib/sidebar-sections.ts'
import { useVisibleModules } from '../lib/use-visible-modules.ts'
import {
  moduleForPath,
  moduleTitle,
  useWorkspace,
  type WorkspaceModule,
} from '../lib/workspace.ts'

import { ApiStatus } from './api-status.tsx'
import { BrandLogo } from './brand-logo.tsx'
import { ContextSwitcher } from './context-switcher.tsx'
import { ModuleSearch } from './module-search.tsx'
import { PageGate } from './page-gate.tsx'
import { ROLE_LABELS } from './role-labels.ts'

/**
 * Qual item do menu é a tela de agora — F248.
 *
 * Era `pathname.startsWith(href)`: "Atendimento" acendia no Painel e nas dez
 * abas da configuração do atendimento, "Robô" e "Robô de preço" acendiam
 * juntos, e numa aba de Configurações nada acendia. Agora é a tela que
 * `moduleForPath` acha (a rota mais longa que casa), e a tela que mora numa
 * aba (`inTab`) acende o item dono das abas.
 */
const menuModuleFor = (pathname: string): WorkspaceModule | null => {
  const here = moduleForPath(pathname)
  if (here === null) return null
  return MODULES.find((entry) => entry.module === here)?.inTab ?? here
}

/**
 * Site Mode shell.
 *
 * Sidebar, topbar and workspace. The sidebar collapses to icons and remembers
 * that per device, because a branch terminal and an office notebook want
 * different answers.
 *
 * The navigation lists only screens that exist. docs/UI_UX.md forbids inventing
 * the rest, so a module appears here the feature that builds it and not before,
 * by adding itself to `lib/modules.tsx`.
 *
 * Três maneiras de chegar numa tela, porque as pessoas chegam com perguntas
 * diferentes. O acesso rápido é o que se faz o dia inteiro. As seções — que
 * abrem, e começam fechadas — respondem "o que existe aqui". O Ctrl/Cmd+K
 * responde "me leve até X", que um menu responde mal: custa varrer tudo para
 * achar uma coisa.
 */

const SIDEBAR_STORAGE_KEY = 'aeris.sidebar-collapsed'

const readCollapsed = (): boolean =>
  window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'

/**
 * Quais seções do índice estão abertas.
 *
 * Fechadas por padrão, e lembradas por dispositivo. Quem trabalha no balcão não
 * é usuário de menu: a lista inteira aberta o tempo todo é uma parede de vinte
 * linhas onde ele procura quatro. O índice continua completo — a diferença é
 * que ele **abre**, em vez de estar sempre aberto.
 */
const GROUPS_STORAGE_KEY = 'aeris.sidebar-groups'

const readOpenGroups = (): ReadonlySet<ModuleGroup> => {
  const raw = window.localStorage.getItem(GROUPS_STORAGE_KEY)
  if (raw === null) return new Set()
  try {
    const parsed: unknown = JSON.parse(raw)
    const stored = Array.isArray(parsed) ? parsed : []
    // Filtrado contra a lista real de grupos: o que está guardado é de uma
    // versão anterior do produto, e um nome que não existe mais não deve virar
    // uma seção fantasma.
    return new Set(MODULE_GROUPS.filter((group) => stored.includes(group)))
  } catch {
    return new Set()
  }
}

/**
 * One entry.
 *
 * The active screen is marked twice — a filled background and a rail on the
 * leading edge. Colour alone is the sort of signal that survives review and
 * then fails on a washed-out counter monitor at an angle, which is exactly
 * where this product runs.
 */
/**
 * Quantas etiquetas estão na fila, ao lado de "Etiquetas" (F136).
 *
 * O trocador manda produtos para a fila e depois não achava a lista: um número
 * no menu diz onde ela está e que tem coisa esperando. Lê o armazenamento do
 * computador — nenhuma requisição.
 */
const LabelQueueCount = ({
  tenantId,
}: {
  tenantId: string | null
}): ReactNode => {
  const items = useSyncExternalStore(subscribeToLabelQueue, () =>
    readLabelQueue(tenantId)
  )
  const total = labelCount(items)
  if (total === 0) return null
  return (
    <Badge
      tone="info"
      className="ml-auto"
      aria-label={`${String(total)} ${total === 1 ? 'etiqueta' : 'etiquetas'} na fila`}
    >
      {total > 999 ? '999+' : total}
    </Badge>
  )
}

const SidebarLink = ({
  item,
  href,
  pathname,
  collapsed,
  onNavigate,
  badge,
}: {
  item: ModuleEntry
  href: string
  pathname: string
  collapsed: boolean
  /** Na gaveta do celular: escolher a tela fecha a gaveta. */
  onNavigate?: (() => void) | undefined
  /** Um contador ao lado do nome — só com o menu aberto. */
  badge?: ReactNode
}): ReactNode => {
  const active = menuModuleFor(pathname) === item.module

  /*
   * Sem `<li>` próprio.
   *
   * Ele trazia um, e o chamador de cima também — então o menu renderizava
   * `<li>` dentro de `<li>`, que o React reclama em voz alta no console e que
   * faz um leitor de tela contar as telas errado. Quem embrulha agora é cada
   * chamador, que é quem sabe se está numa lista ou dentro de um item dela.
   */
  return (
    <Tooltip content={item.label} disabled={!collapsed} delay={0}>
      <Link
        to={href}
        onClick={onNavigate}
        aria-current={active ? 'page' : undefined}
        className={cn(
          'relative flex h-8 items-center gap-2.5 rounded-(--radius-md) px-2',
          'text-(length:--text-sm) text-(--sidebar-text)',
          'transition-colors duration-(--duration-fast)',
          'hover:bg-(--sidebar-item-hover) hover:text-(--sidebar-text-active)',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white',
          active &&
            'bg-(--sidebar-item-active) font-(--weight-medium) text-(--sidebar-text-active)',
          active &&
            'before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-0.5 before:rounded-full before:bg-(--sidebar-text-active)',
          collapsed && 'justify-center px-0'
        )}
      >
        <item.Icon
          aria-hidden="true"
          className={cn(
            'shrink-0',
            collapsed ? 'h-[18px] w-[18px]' : 'h-4 w-4'
          )}
        />
        {collapsed ? null : <span className="truncate">{item.label}</span>}
        {collapsed ? null : badge}
      </Link>
    </Tooltip>
  )
}

/**
 * Uma linha do acesso rápido do balcão.
 *
 * Maior que um item de menu de propósito: no desktop ela é apertada com o
 * mouse, às pressas, com um cliente na frente — e a referência que o dono
 * mostrou é exatamente isto, quatro alvos grandes com ícone e nome.
 */
const CounterLink = ({
  item,
  href,
  pathname,
  collapsed,
  onNavigate,
  children,
}: {
  item: ModuleEntry
  href: string
  pathname: string
  collapsed: boolean
  onNavigate?: (() => void) | undefined
  /** As telas que moram sob esta — Etiquetas, Preços e Estoque sob Produtos. */
  children?: ReactNode
}): ReactNode => {
  const active = menuModuleFor(pathname) === item.module

  return (
    <li>
      <Tooltip content={item.label} disabled={!collapsed} delay={0}>
        <Link
          to={href}
          onClick={onNavigate}
          aria-current={active ? 'page' : undefined}
          className={cn(
            'relative flex items-center gap-3 rounded-(--radius-md) px-2',
            collapsed ? 'h-11 justify-center px-0' : 'h-11',
            'text-(length:--text-md) text-(--sidebar-text)',
            'transition-colors duration-(--duration-fast)',
            'hover:bg-(--sidebar-item-hover) hover:text-(--sidebar-text-active)',
            'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white',
            active &&
              'bg-(--sidebar-item-active) font-(--weight-medium) text-(--sidebar-text-active)'
          )}
        >
          <item.Icon aria-hidden="true" className="h-5 w-5 shrink-0" />
          {collapsed ? null : <span className="truncate">{item.label}</span>}
        </Link>
      </Tooltip>
      {children}
    </li>
  )
}

export const AppShell = ({ children }: { children: ReactNode }): ReactNode => {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const [searchOpen, setSearchOpen] = useState(false)
  /** A gaveta do menu no celular e no tablet (F132). */
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [openGroups, setOpenGroups] = useState(readOpenGroups)
  const workspace = useWorkspace()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  /*
   * O nome da tela na aba do navegador — F248. Era sempre "Aeris One": com
   * Vendas, Estoque e Atendimento abertos em três abas, não dava para saber
   * qual era qual.
   */
  useEffect(() => {
    const here = moduleForPath(pathname)
    document.title =
      here === null ? 'Aeris One' : `${moduleTitle(here)} · Aeris One`
  }, [pathname])
  /*
   * O enquadramento que a rota pediu — F085.
   *
   * Três categorias, e a decisão continua sendo do SHELL: a rota só declara
   * qual delas quer (`staticData: { chrome: 'workspace' }` em `router.tsx`).
   * O shell nunca compara `pathname` com um caminho fixo — isso acopla a
   * moldura a uma URL, e a próxima tela que precisar do mesmo enquadramento
   * teria de ser lembrada aqui dentro.
   *
   * `matches.at(-1)` é a rota FOLHA, a que está de fato desenhada. Uma aba de
   * configuração herda o enquadramento da página que a contém, que é o que se
   * quer: quem manda é a folha, e ela não declara nada.
   */
  const chrome = useRouterState({
    select: (state) => state.matches.at(-1)?.staticData.chrome,
  })
  const isWorkspace = chrome === 'workspace'
  const { session, signOut, showsPage, isPlatformAdmin } = useAuth()
  const navigate = useNavigate()

  const toggleSidebar = useCallback(() => {
    setCollapsed((previous) => {
      const next = !previous
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next))
      return next
    })
  }, [])

  const toggleGroup = useCallback((group: ModuleGroup) => {
    setOpenGroups((previous) => {
      const next = new Set(previous)
      if (next.has(group)) next.delete(group)
      else next.add(group)
      window.localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify([...next]))
      return next
    })
  }, [])

  // Ctrl/Cmd + B collapses and Ctrl/Cmd + K goes somewhere. Both are the
  // conventional shortcut for what they do, and ERP work is keyboard-first
  // (docs/UI_UX.md).
  useScopedKeydown((event: KeyboardEvent): void => {
    if (!event.ctrlKey && !event.metaKey) return
    const key = event.key.toLowerCase()

    if (key === 'b') {
      event.preventDefault()
      toggleSidebar()
    } else if (key === 'k') {
      event.preventDefault()
      setSearchOpen(true)
    }
  })

  /*
   * As telas desta pessoa, SEM as abas.
   *
   * As telas que viraram aba saem daqui, e só daqui (F068): continuam no
   * Ctrl+K e na grade do Início, porque uma tela que some do menu e da busca
   * ao mesmo tempo deixou de existir. O resto da regra — papel, telas que o
   * admin deixou marcadas, loja que não conta estoque — é uma só para a barra
   * lateral, a busca, o Início e os menus do Modo PC: `use-visible-modules.ts`
   * (F138).
   */
  const visible = useVisibleModules({ includeTabs: false })

  /**
   * O acesso rápido, agora em todo lugar.
   *
   * Era só do aplicativo instalado. Passou a valer também no navegador porque
   * quem usa o Aeris o dia inteiro é a mesma pessoa nos dois — e ela não é
   * usuária de menu: pede as quatro coisas que faz sempre, grandes, e o resto
   * quando precisar.
   */
  /*
   * Acesso rápido, grupos e filhos — `lib/sidebar-sections.ts` (F132). O que
   * está no acesso rápido não se repete no grupo, e o filho cujo pai a pessoa
   * não vê sobe para o grupo em vez de sumir.
   */
  const { counter, grouped, nested } = useMemo(
    () => sidebarSections(visible, COUNTER_MODULES),
    [visible]
  )

  const home = useMemo(
    () => visible.find((item) => item.module === 'inicio'),
    [visible]
  )

  /*
   * O Início abre o acesso rápido (F215).
   *
   * Ele tinha saído da lista porque o logotipo já leva a ele, mas os vendedores
   * não sabiam disso: "não tem a página de início na sidebar". E é no Início que
   * mora "Minhas vendas", o que eles querem ver no meio do dia.
   */
  const quick = useMemo(
    () => (home === undefined ? counter : [home, ...counter]),
    [home, counter]
  )

  /**
   * O grupo da tela aberta, que abre sozinho.
   *
   * Sem isto, entrar em Financeiro pelo Ctrl+K deixaria o menu apontando para
   * lugar nenhum: a seção que contém a tela atual estaria fechada, e o menu
   * diria que a pessoa não está em canto algum.
   */
  const activeGroup = useMemo(() => {
    const current = menuModuleFor(pathname)
    const here = visible.find(
      (item) => item.module !== 'inicio' && item.module === current
    )
    return here?.group
  }, [visible, pathname])

  const closeDrawer = (): void => {
    setDrawerOpen(false)
  }

  const tenantId = session?.context?.tenantId ?? null

  /*
   * Os filhos de uma tela, recuados sob ela — no grupo E no acesso rápido.
   *
   * Antes só o grupo desenhava os filhos, e Produtos mora no acesso rápido:
   * Etiquetas, Preços e Estoque estavam calculados em `nested` e não saíam em
   * lugar nenhum do menu (F136, "não acho a lista de etiquetas"). Recolhido não
   * há onde recuar, então o aninhamento desaparece junto com os rótulos — as
   * telas continuam no Ctrl+K e no Início.
   */
  const childrenOf = (module: ModuleEntry['module'], compact: boolean) => {
    const children = nested.get(module) ?? []
    if (compact || children.length === 0) return null
    return (
      <ul className="ml-4 border-l border-(--sidebar-border) pl-1">
        {children.map((child) => (
          <li key={child.module}>
            <SidebarLink
              item={child}
              collapsed={compact}
              href={workspace.routeFor(child.module)}
              pathname={pathname}
              onNavigate={closeDrawer}
              badge={
                child.module === 'etiquetas' ? (
                  <LabelQueueCount tenantId={tenantId} />
                ) : undefined
              }
            />
          </li>
        ))}
      </ul>
    )
  }

  /*
   * O miolo da barra lateral, desenhado em dois lugares: a barra fixa (que
   * recolhe em ícones) e a gaveta do celular (sempre aberta, sem o botão de
   * recolher). Uma função, e não duas cópias do JSX, para as duas não
   * divergirem na próxima tela que entrar no menu.
   */
  const navBody = (compact: boolean, inDrawer: boolean): ReactNode => (
    <>
      {/*
          A marca leva para o Início.

          É o que um logotipo no canto superior esquerdo faz em todo lugar, e
          por isso o Início saiu da lista: uma linha de menu para a tela que o
          logotipo já abre é uma linha gasta.
        */}
      <Link
        to={home === undefined ? '/' : workspace.routeFor('inicio')}
        onClick={closeDrawer}
        aria-label="Início"
        // A marca é o link do Início, e um link que leva para onde a pessoa
        // já está tem de dizer isso — senão o menu inteiro fica sem nenhuma
        // marca de "você está aqui" na tela inicial.
        aria-current={
          pathname.startsWith(workspace.routeFor('inicio')) ? 'page' : undefined
        }
        className={cn(
          'flex h-16 shrink-0 items-center border-b border-(--sidebar-border)',
          'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white',
          compact ? 'justify-center px-2' : 'px-3'
        )}
      >
        {/*
            A marca da loja quando ela tem uma (F169), e a da Aeris quando não.
            O filtro que pinta o PNG de branco vive dentro de `BrandLogo`,
            porque ele é do ASSET e não deste lugar.
        */}
        {compact ? (
          <BrandLogo variant="mark" className="h-9 w-9 rounded-(--radius-sm)" />
        ) : (
          <BrandLogo variant="horizontal" className="h-9 w-auto" />
        )}
      </Link>

      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto p-2">
        {/*
            O menu começa pelo balcão.

            Quatro alvos grandes, que é a forma do sistema que eles já usam. O
            índice inteiro continua logo abaixo, em seções que abrem — a pessoa
            que vende o dia inteiro não deveria ter que ler vinte linhas para
            achar as quatro dela.
          */}
        {quick.length > 0 ? (
          <div className="flex flex-col gap-0.5">
            {compact ? null : (
              <h2 className="px-2 pt-1 pb-1 text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide text-(--sidebar-text) opacity-60">
                Acesso rápido
              </h2>
            )}
            {/*
                A lista é nomeada porque as quatro do balcão também estão no
                índice abaixo. Sem o nome, "o link Produtos" é ambíguo para quem
                navega por leitor de tela — e para quem escreve um teste.
              */}
            <ul aria-label="Acesso rápido" className="flex flex-col gap-0.5">
              {quick.map((item) => (
                <CounterLink
                  key={item.module}
                  item={item}
                  collapsed={compact}
                  href={workspace.routeFor(item.module)}
                  pathname={pathname}
                  onNavigate={closeDrawer}
                >
                  {childrenOf(item.module, compact)}
                </CounterLink>
              ))}
            </ul>
          </div>
        ) : null}

        {MODULE_GROUPS.map((group) => {
          const items = grouped.get(group) ?? []
          if (items.length === 0) return null

          /*
              Recolhido não há onde pendurar um rótulo, e uma seção fechada
              seria um ícone que some. Então o trilho de ícones continua
              mostrando tudo, separado por um fio.
            */
          const open = compact || openGroups.has(group) || activeGroup === group

          return (
            <div key={group} className="flex flex-col gap-0.5">
              {compact ? (
                <div
                  aria-hidden="true"
                  className="mx-2 my-1 h-px bg-(--sidebar-border)"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    toggleGroup(group)
                  }}
                  aria-expanded={open}
                  className={cn(
                    'flex h-9 items-center gap-1.5 rounded-(--radius-md) px-2',
                    'text-(length:--text-xs) font-(--weight-semibold) uppercase tracking-wide',
                    'text-(--sidebar-text) opacity-70',
                    'transition-colors hover:bg-(--sidebar-item-hover) hover:opacity-100',
                    'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white'
                  )}
                >
                  {open ? (
                    <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />
                  )}
                  {group}
                </button>
              )}

              {open ? (
                <ul aria-label={group} className="flex flex-col gap-0.5">
                  {items.map((item) => (
                    <li key={item.module}>
                      <SidebarLink
                        item={item}
                        collapsed={compact}
                        href={workspace.routeFor(item.module)}
                        pathname={pathname}
                        onNavigate={closeDrawer}
                        badge={
                          item.module === 'etiquetas' ? (
                            <LabelQueueCount tenantId={tenantId} />
                          ) : undefined
                        }
                      />
                      {childrenOf(item.module, compact)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          )
        })}
      </div>

      {inDrawer ? null : (
        <div className="border-t border-(--sidebar-border) p-2">
          <Tooltip
            content={compact ? 'Expandir menu' : 'Recolher menu'}
            disabled={false}
          >
            <button
              type="button"
              onClick={toggleSidebar}
              aria-expanded={!compact}
              aria-label={
                compact
                  ? 'Expandir menu lateral (Ctrl+B)'
                  : 'Recolher menu lateral (Ctrl+B)'
              }
              className={cn(
                'flex h-8 w-full items-center gap-2.5 rounded-(--radius-md) px-2',
                'text-(length:--text-sm) text-(--sidebar-text)',
                'transition-colors hover:bg-(--sidebar-item-hover) hover:text-(--sidebar-text-active)',
                'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white',
                compact && 'justify-center px-0'
              )}
            >
              {compact ? (
                <ChevronsRight aria-hidden="true" className="h-4 w-4" />
              ) : (
                <>
                  <ChevronsLeft aria-hidden="true" className="h-4 w-4" />
                  <span>Recolher</span>
                </>
              )}
            </button>
          </Tooltip>
        </div>
      )}
    </>
  )

  return (
    /*
     * `100dvh`, nunca `100vh`.
     *
     * A altura do shell é o que decide onde a rolagem acontece: este elemento
     * é `overflow-hidden`, e quem rola é o `<main>` lá embaixo. Com `100vh`
     * num navegador de barra de endereço retrátil — o tablet do balcão, o
     * celular, o Chrome do Android —, o shell fica MAIOR que a área visível.
     * O documento ganha então uma rolagem residual própria, e a barra lateral
     * e o conteúdo passam a rolar com limites diferentes: a página "quebra"
     * ao rolar, sobra uma faixa em branco no fim e o rodapé do conteúdo fica
     * abaixo da dobra.
     *
     * O design system já sabia disso e o shell não seguiu: ver o comentário
     * em `overlay.tsx`, sobre `max-h-[calc(100dvh-2rem)]` no diálogo —
     * "a phone's address bar makes the two differ by exactly enough to hide a
     * Salvar button".
     */
    <div className="mt-(--support-banner-h) flex h-[calc(100dvh-var(--support-banner-h))] overflow-hidden bg-(--surface-base)">
      <a href="#workspace" className="aeris-skip-link">
        Ir para o conteúdo
      </a>

      {/*
        A barra fixa, do tablet grande para cima. Abaixo de `lg` ela vira a
        gaveta do ☰ (F132): 224px fixos comiam metade da tela de um celular.
      */}
      <nav
        aria-label="Navegação principal"
        className={cn(
          'hidden shrink-0 flex-col bg-(--sidebar-bg) lg:flex',
          'transition-[width] duration-(--duration-normal) ease-(--ease-out)',
          collapsed ? 'w-14' : 'w-56'
        )}
      >
        {navBody(collapsed, false)}
      </nav>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent title="Menu" className="bg-(--sidebar-bg) lg:hidden">
          <nav
            aria-label="Navegação principal"
            className="flex min-h-0 flex-1 flex-col"
          >
            {navBody(false, true)}
          </nav>
        </SheetContent>
      </Sheet>

      <div className="flex min-w-0 flex-1 flex-col">
        <header
          className={cn(
            // A mesma altura da faixa da marca, para as duas réguas do topo
            // fecharem numa linha só.
            'flex h-16 shrink-0 items-center gap-2 px-2 sm:gap-3 sm:px-4',
            'border-b border-(--border-default) bg-(--surface)'
          )}
        >
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 lg:hidden"
            aria-label="Abrir o menu"
            aria-expanded={drawerOpen}
            onClick={() => {
              setDrawerOpen(true)
            }}
          >
            <Menu aria-hidden="true" className="h-5 w-5" />
          </Button>

          <ContextSwitcher />

          {/* Goes to a screen, not into the data: searching products and
              people from here is a later feature, and the label says which of
              the two this is rather than promising both. */}
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setSearchOpen(true)
            }}
            leadingIcon={<Search aria-hidden="true" className="h-3.5 w-3.5" />}
            className="hidden w-56 justify-start text-(--text-muted) md:inline-flex"
          >
            Ir para uma tela…
            <kbd className="ml-auto rounded-(--radius-sm) border border-(--border-subtle) px-1 font-(--font-mono) text-(length:--text-xs)">
              Ctrl K
            </kbd>
          </Button>
          {/* No celular a busca é só o ícone: a barra do topo não cabe. */}
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            aria-label="Ir para uma tela (Ctrl K)"
            onClick={() => {
              setSearchOpen(true)
            }}
          >
            <Search aria-hidden="true" className="h-4 w-4" />
          </Button>

          <div className="ml-auto flex items-center gap-2">
            <ApiStatus />
            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  /*
                   * O nome acessível deste botão é o nome da pessoa, o que está
                   * certo para quem usa leitor de tela e é inútil para um teste:
                   * o e2e teria que saber quem o seed criou. O `data-testid` dá
                   * um nome estável sem mexer no que a tecnologia assistiva lê.
                   */
                  data-testid="account-menu"
                  leadingIcon={
                    <UserRound aria-hidden="true" className="h-4 w-4" />
                  }
                  aria-label={session?.user.displayName ?? 'Conta'}
                >
                  {/* O nome some no celular; o ícone e o rótulo ficam. */}
                  <span className="hidden sm:inline">
                    {session?.user.displayName ?? ''}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64">
                <div className="flex flex-col gap-0.5 px-2 py-1.5">
                  <span className="truncate text-(length:--text-sm) font-(--weight-medium) text-(--text-primary)">
                    {session?.user.displayName ?? ''}
                  </span>
                  <span className="truncate text-(length:--text-xs) text-(--text-muted)">
                    {session?.user.email ?? ''}
                  </span>
                  {session?.context === null ||
                  session?.context === undefined ? null : (
                    <span className="mt-1 text-(length:--text-xs) text-(--text-secondary)">
                      {session.context.tenantName} ·{' '}
                      {ROLE_LABELS[session.context.role]}
                    </span>
                  )}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    void navigate({ to: '/trocar-senha' })
                  }}
                >
                  <KeyRound aria-hidden="true" className="h-4 w-4" />
                  Trocar senha
                </DropdownMenuItem>
                {/*
                  O console da plataforma (F119).

                  Aqui, e não no menu lateral, pelo mesmo motivo de "Aeris no
                  computador": não é uma tela do trabalho da loja, e uma linha
                  permanente na navegação para ela seria uma linha gasta para
                  todo mundo — inclusive para os lojistas, que nem a enxergam.
                  Quem opera a instalação abre isto algumas vezes por semana.
                */}
                {isPlatformAdmin ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      void navigate({ to: '/plataforma' })
                    }}
                  >
                    <Building2 aria-hidden="true" className="h-4 w-4" />
                    Contas da plataforma
                  </DropdownMenuItem>
                ) : null}
                {/*
                  Instalar o Aeris na máquina (F043).

                  Aqui e não no menu lateral: é uma coisa que se faz uma vez por
                  computador, e uma linha permanente na navegação para isso é
                  uma linha gasta. Continua aparecendo dentro do aplicativo
                  instalado — o caso real é a pessoa estar no PC do balcão e
                  querer o Aeris no caixa 2.
                */}
                {showsPage('baixar') ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      void navigate({ to: '/baixar' })
                    }}
                  >
                    <Download aria-hidden="true" className="h-4 w-4" />
                    Aeris no computador
                  </DropdownMenuItem>
                ) : null}
                {/*
                 * Só no aplicativo instalado (F138). No navegador não há área
                 * de trabalho, e oferecer uma chave que não faz nada é pior do
                 * que não oferecer. A barra lateral só aparece com o Modo PC
                 * desligado, então aqui a opção é sempre a de ligar; a de
                 * voltar mora na barra de tarefas.
                 */}
                {desktopShellAvailable() ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      setPcMode(true)
                    }}
                  >
                    <MonitorSmartphone aria-hidden="true" className="h-4 w-4" />
                    Usar o Modo PC
                  </DropdownMenuItem>
                ) : null}
                {/* O glossário do produto (F156): o que cada situação e cada
                    cor querem dizer. Alcançável por todo mundo. */}
                <DropdownMenuItem
                  onSelect={() => {
                    void navigate({ to: '/ajuda' })
                  }}
                >
                  <CircleHelp aria-hidden="true" className="h-4 w-4" />
                  Situações e cores
                </DropdownMenuItem>
                {/*
                 * A configuração da máquina, alcançável (F155).
                 *
                 * `/servidor` existe desde a F040 e não tinha link nenhum — e o
                 * aplicativo instalado não tem barra de endereço, então trocar
                 * o servidor de uma loja exigia editar `settings.json` à mão.
                 *
                 * `window.location`, e não o roteador: a tela vive fora do
                 * shell de propósito, porque quem troca de servidor pode estar
                 * apontando para um que não responde.
                 */}
                {desktopShellAvailable() ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      window.location.assign('/servidor')
                    }}
                  >
                    <ServerCog aria-hidden="true" className="h-4 w-4" />
                    Servidor e Edge
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => {
                    void signOut()
                  }}
                >
                  <LogOut aria-hidden="true" className="h-4 w-4" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/*
         * Duas molduras, uma decisão.
         *
         * Uma PÁGINA rola por dentro do `<main>` e respira num padding de
         * 20px. Um ESPAÇO DE TRABALHO recebe a área exata entre a topbar e o
         * fim da janela — sem padding, e `overflow-hidden`, porque quem
         * gerencia a rolagem lá dentro são as regiões dele (a lista de
         * conversas, a thread), não a página. Sem o `hidden`, uma thread longa
         * empurraria a tela inteira e o campo de resposta sairia da dobra —
         * exatamente o defeito que a F081 corrigiu de outro jeito.
         */}
        <main
          id="workspace"
          tabIndex={-1}
          className={cn(
            'min-h-0 flex-1',
            isWorkspace ? 'overflow-hidden' : 'overflow-auto p-3 sm:p-5'
          )}
        >
          {children}
        </main>
      </div>

      <ModuleSearch open={searchOpen} onOpenChange={setSearchOpen} />
    </div>
  )
}

/**
 * A janela do Modo PC — a mesma tela, sem barra lateral e sem topbar.
 *
 * O enquadramento segue a MESMA regra do shell (F085): uma página rola e tem
 * padding; um espaço de trabalho recebe a janela inteira e contém a própria
 * rolagem. Duplicar a regra aqui com um `p-5` fixo faria o Atendimento aberto
 * em janela ganhar uma moldura que ele não tem no navegador — a mesma tela com
 * dois comportamentos, que é o que o comentário abaixo já existia para evitar.
 */
export const ModuleWindowMain = (): ReactNode => {
  const chrome = useRouterState({
    select: (state) => state.matches.at(-1)?.staticData.chrome,
  })

  return (
    <main
      className={cn(
        'mt-(--support-banner-h) h-[calc(100dvh-var(--support-banner-h))] bg-(--surface-base)',
        chrome === 'workspace' ? 'overflow-hidden' : 'overflow-auto p-5'
      )}
    >
      <PageGate>
        <Outlet />
      </PageGate>
    </main>
  )
}
